/**
 * Matrix measure aggregation + display currency (same rules as ReportPreview / reportCurrencyAggregate).
 */

import jwt_decode from 'jwt-decode';
import { formatCurrency, formatNumber, formatPercent } from '../../../utils/formatUtils';
import {
  reportFieldIsCurrencyLike,
  reportFieldIsPercentLike,
  shouldConvertReportMeasureAtRow,
  convertAmountToBase,
  getRowCurrencyAndRateFromReportRow,
  resolveEffectiveExchangeRate,
  formatAggregatedDisplayForReport,
  unwrapReportScalar,
} from '../../../utils/reportCurrencyAggregate';
import { logReportCurrency } from '../debug/reportCurrencyDebug';

let pivotValueDebugCount = 0;

export function resolveReportCurrency(fallbackCurrency = 'INR') {
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const decoded = jwt_decode(token);
      if (decoded.currency) return decoded.currency;
    }
  } catch {
    /* ignore */
  }
  return fallbackCurrency;
}

function getMeasureRawValue(row, field) {
  const tableName = field.object_name || 'main';
  const aliased = `${tableName}_${field.field_name}`;
  return row[aliased] ?? row[field.field_name];
}

function extractNumericFromFieldData(fieldData) {
  if (fieldData == null) return null;
  const raw =
    typeof fieldData === 'object' && fieldData !== null
      ? unwrapReportScalar(fieldData) ?? fieldData.value
      : fieldData;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Numeric value to aggregate in a matrix cell bucket (base currency when enabled).
 */
export function getMeasurePivotValue(row, measure, currencyOptions = {}) {
  const { baseCurrencyCode = null, exchangeRateLookup = null } = currencyOptions;

  if (measure.id === '__count__' || measure.field_name === '__count__') {
    return 1;
  }

  const fieldData = getMeasureRawValue(row, measure);
  const num = extractNumericFromFieldData(fieldData);
  if (num == null) return null;

  if (
    baseCurrencyCode &&
    (reportFieldIsCurrencyLike(measure) ||
      shouldConvertReportMeasureAtRow(measure, row, baseCurrencyCode))
  ) {
    const tableName = measure.object_name || 'main';
    const { currency, rate } = getRowCurrencyAndRateFromReportRow(row, tableName);
    const effectiveRate = resolveEffectiveExchangeRate(
      currency,
      rate,
      exchangeRateLookup
    );
    const converted = convertAmountToBase(num, currency, effectiveRate, baseCurrencyCode);
    if (pivotValueDebugCount < 3) {
      pivotValueDebugCount += 1;
      logReportCurrency('getMeasurePivotValue.convert', {
        n: pivotValueDebugCount,
        measure: measure.field_name,
        data_type: measure.data_type,
        isCurrencyLike: reportFieldIsCurrencyLike(measure),
        rawNum: num,
        rowCurrency: currency,
        rowRate: rate,
        effectiveRate,
        baseCurrencyCode,
        converted,
      });
    }
    return converted;
  }

  if (pivotValueDebugCount < 3) {
    pivotValueDebugCount += 1;
    logReportCurrency('getMeasurePivotValue.noConvert', {
      n: pivotValueDebugCount,
      measure: measure.field_name,
      data_type: measure.data_type,
      isCurrencyLike: reportFieldIsCurrencyLike(measure),
      hasBase: Boolean(baseCurrencyCode),
      rawNum: num,
    });
  }

  return num;
}

function formatMatrixFallbackValue(result, field, aggregation) {
  const agg = (aggregation || 'sum').toLowerCase();
  if (agg === 'count') return String(result);

  if (reportFieldIsPercentLike(field)) {
    const decimalPlaces =
      field?.scale !== undefined && field?.scale !== null && field?.scale !== ''
        ? parseInt(field.scale, 10)
        : 2;
    return formatPercent(result, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });
  }

  const dt = String(field?.data_type || '').toLowerCase();
  if (dt === 'currency' || reportFieldIsCurrencyLike(field)) {
    const decimalPlaces =
      field.scale !== undefined && field.scale !== null && field.scale !== ''
        ? parseInt(field.scale, 10)
        : 2;
    const displayCode = resolveReportCurrency('INR');
    return formatCurrency(result, displayCode, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });
  }
  if (
    ['number', 'decimal', 'integer', 'float', 'double', 'rollupsummary'].includes(dt)
  ) {
    return formatNumber(result, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return result.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/**
 * Format aggregated matrix cell (currency symbol via base or token fallback).
 */
let formatCellDebugCount = 0;

export function formatMatrixMeasureCell(value, measure, aggregation, currencyOptions = {}) {
  if (value == null || !Number.isFinite(value)) return '—';

  const { baseCurrencyCode = null } = currencyOptions;

  const out = formatAggregatedDisplayForReport(
    value,
    measure,
    aggregation,
    baseCurrencyCode || null,
    formatMatrixFallbackValue
  );

  if (formatCellDebugCount < 2) {
    formatCellDebugCount += 1;
    logReportCurrency('formatMatrixMeasureCell', {
      n: formatCellDebugCount,
      value,
      baseCurrencyCode,
      measure: measure?.field_name,
      data_type: measure?.data_type,
      isCurrencyLike: reportFieldIsCurrencyLike(measure),
      aggregation,
      output: out,
    });
  }

  return out;
}
